/**
 * سمات السلّة المملوكة لجوجل (هويّة جلسة GA4 + نقرة Ads + UTM).
 *
 * مصدر حقيقة واحد لأمرين:
 *  1. بناء السمات الحاليّة حسب الموافقة القائمة.
 *  2. قائمة المفاتيح التي يجب **حذفها** من السلّة عند سحب الموافقة.
 *
 * دلالة الحذف في شوبيفاي: `cartAttributesUpdate` يستبدل مجموعة السمات
 * بالكامل، فالحذف = إعادة إرسال المجموعة بلا هذه المفاتيح (لا قيمة فارغة).
 */

import { ADS_CLICK_ATTR_KEYS, buildAdsClickAttributes } from "./adsClickId";
import { UTM_ATTR_KEYS, buildUtmAttributes } from "./utmCapture";
import {
  GA_SESSION_ATTR_KEYS,
  buildGaSessionAttributes,
  getCachedGaSession,
  primeGaSession,
} from "./gaSession";

export type CartAttribute = { key: string; value: string };

export const GOOGLE_OWNED_CART_ATTR_KEYS: readonly string[] = [
  ...GA_SESSION_ATTR_KEYS,
  ...ADS_CLICK_ATTR_KEYS,
  ...UTM_ATTR_KEYS,
];

const OWNED = new Set<string>(GOOGLE_OWNED_CART_ATTR_KEYS);

/** هل هذه السمة مملوكة لجوجل (أي تُبنى كاملةً من الموافقة الحاليّة)؟ */
export function isGoogleOwnedCartAttr(key: string): boolean {
  return OWNED.has(key);
}

/**
 * السمات الحاليّة **بلا أيّ انتظار**.
 *
 * التتبّع لا يجوز أن يؤخّر الإضافة للسلّة ولا الذهاب للدفع: نقرأ آخر لقطة
 * جلسة حقيقيّة من الذاكرة فوراً، ونطلب تسخين اللقطة في الخلفيّة للمرّة
 * القادمة. سكربت جوجل البطيء أو المحجوب يعني سمات أقلّ فقط، لا تجميد
 * الشراء. لا تلفيق معرّفات مطلقاً.
 */
export function buildGoogleCartAttributes(): CartAttribute[] {
  let session: ReturnType<typeof getCachedGaSession> = null;
  try {
    session = getCachedGaSession();
    // تسخين للمرّة القادمة (لا انتظار).
    primeGaSession();
  } catch {
    /* لا تُعطّل السلّة */
  }

  const attrs: CartAttribute[] = [];
  try {
    attrs.push(...buildAdsClickAttributes());
  } catch {
    /* لا تُعطّل السلّة */
  }
  try {
    attrs.push(...buildUtmAttributes());
  } catch {
    /* لا تُعطّل السلّة */
  }
  try {
    attrs.push(...buildGaSessionAttributes(session));
  } catch {
    /* لا تُعطّل السلّة */
  }
  return attrs;
}


/**
 * يدمج السمات الحاليّة مع سمات السلّة القائمة:
 * يُبقي كلّ سمة غير مملوكة لجوجل كما هي (الهديّة/الزائر/السفير/المخصّصة)،
 * ويستبدل المملوكة بما تسمح به الموافقة الآن (فتُحذف عند السحب).
 */
export function mergeGoogleCartAttributes(
  existing: readonly CartAttribute[],
  fresh: readonly CartAttribute[],
): CartAttribute[] {
  const preserved = existing.filter((attr) => !isGoogleOwnedCartAttr(attr.key));
  return [...preserved, ...fresh];
}
